import type {
    AidPreferenceEntityObject,
    AidPreferenceQueryParams,
} from "src/app/features/EmvKernelProfile/subFeatures/AidPreference/services/server/AidPreferenceQueryTypes";
import { getAssignedDataGroup } from "../../../../../../../core/router/auth";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { baseAuthApi } from "../../../../../../../core/service/baseApi";
import type { Dto } from "../../../../../../../core/types/be/Dto";
import type { AidPreferenceEntity } from "../../types/AidPreferenceEntity";
import { AidPreferenceDtoToEntity } from "../mappers/AidPreferenceDtoToEntity";
import { AidPreferenceEntityToDto } from "../mappers/AidPreferenceEntityToDto";

const AidPreferenceApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        AidPreferenceLoad: builder.query<AidPreferenceEntity, AidPreferenceQueryParams>({
            providesTags: [ResourceName.AID_PREFERENCES],
            query: (aidPreferenceQueryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${aidPreferenceQueryParams.emvKernelProfileId}/${ResourceName.AID_PREFERENCES}/${aidPreferenceQueryParams.id}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return AidPreferenceDtoToEntity(response);
            },
        }),
        AidPreferenceSave: builder.mutation<AidPreferenceEntity, AidPreferenceEntityObject>({
            query: (aidPreferenceEntityObject) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${aidPreferenceEntityObject.aidPreferenceQuery.emvKernelProfileId}/${ResourceName.AID_PREFERENCES}`;
                const method = "POST";

                const body = AidPreferenceEntityToDto(aidPreferenceEntityObject.aidPreferenceFormData);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.AID_PREFERENCES],
        }),
        AidPreferenceUpdate: builder.mutation<AidPreferenceEntity, AidPreferenceEntityObject>({
            query: (aidPreferenceEntityObject) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${aidPreferenceEntityObject.aidPreferenceQuery.emvKernelProfileId}/${ResourceName.AID_PREFERENCES}/${aidPreferenceEntityObject.aidPreferenceQuery.id}`;
                const method = "PUT";

                const body = AidPreferenceEntityToDto(aidPreferenceEntityObject.aidPreferenceFormData);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.AID_PREFERENCES],
        }),
        AidPreferenceDelete: builder.mutation<AidPreferenceEntity, AidPreferenceQueryParams>({
            query: (aidPreferenceQueryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${aidPreferenceQueryParams.emvKernelProfileId}/${ResourceName.AID_PREFERENCES}/${aidPreferenceQueryParams.id}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: [ResourceName.AID_PREFERENCES],
        }),
    }),
});

export const {
    useLazyAidPreferenceLoadQuery,
    useAidPreferenceSaveMutation,
    useAidPreferenceUpdateMutation,
    useAidPreferenceDeleteMutation,
} = AidPreferenceApi;
